// Read-only staff matrix preview matching StaffPlanGrid layout.
// Discipline × Channel rows; level-group columns each split On / Hub.

import { useMemo, type ReactNode } from 'react';
import clsx from 'clsx';
import { LEVEL_GROUPS, resolveCellSplit, type LevelGroup } from '@rc/engine';
import type { LevelId, Model, StaffPlanCell } from '@rc/engine/types';

interface Props {
  model: Model;
  cells: StaffPlanCell[];
}

interface RowKey {
  disciplineId: string;
  channelId: string;
}

const ROW_KEY = (r: RowKey) => `${r.disciplineId}::${r.channelId}`;
const CELL_KEY = (disciplineId: string, channelId: string, levelId: LevelId) =>
  `${disciplineId}::${channelId}::${levelId}`;

function defaultRowsForModel(model: Model): RowKey[] {
  const channels = (model.channels || []).filter((c) => c.active !== false);
  const out: RowKey[] = [];
  for (const d of model.disciplines || []) {
    if (d.active === false) continue;
    const filterIds = d.juniorRatio?.channelIdFilter;
    const cat = d.juniorRatio?.channelCategoryFilter;
    const driver = d.juniorRatio?.driver;
    if (driver === 'totalBudget' && !filterIds && !cat) {
      out.push({ disciplineId: d.id, channelId: 'all' });
      continue;
    }
    const eligible = channels.filter((ch) => {
      if (filterIds && filterIds.length > 0) return filterIds.includes(ch.id);
      if (cat) return ch.category === cat;
      return true;
    });
    if (eligible.length === 0) {
      out.push({ disciplineId: d.id, channelId: 'all' });
    } else {
      for (const ch of eligible) out.push({ disciplineId: d.id, channelId: ch.id });
    }
  }
  return out;
}

function fmtCell(n: number): string {
  return n > 0 ? n.toFixed(1) : '·';
}

export default function StaffPlanMatrixPreview({ model, cells }: Props) {

  const rows = useMemo<RowKey[]>(() => {
    const defaults = defaultRowsForModel(model);
    const seen = new Set(defaults.map(ROW_KEY));
    const extras: RowKey[] = [];
    for (const c of cells) {
      const k = ROW_KEY(c);
      if (!seen.has(k)) {
        seen.add(k);
        extras.push({ disciplineId: c.disciplineId, channelId: c.channelId });
      }
    }
    return [...defaults, ...extras];
  }, [cells, model]);

  const cellByKey = useMemo(() => {
    const map = new Map<string, StaffPlanCell>();
    for (const c of cells) {
      map.set(CELL_KEY(c.disciplineId, c.channelId, c.levelId), c);
    }
    return map;
  }, [cells]);

  const discLabel = (id: string) =>
    model.disciplines.find((d) => d.id === id)?.label || id;
  const chanLabel = (id: string) =>
    id === 'all' ? 'All channels' : model.channels.find((c) => c.id === id)?.label || id;

  const splitFor = (r: RowKey, group: LevelGroup) => {
    let on = 0;
    let hub = 0;
    for (const levelId of group.levelIds) {
      const c = cellByKey.get(CELL_KEY(r.disciplineId, r.channelId, levelId));
      if (!c) continue;
      const s = resolveCellSplit(c);
      on += s.on;
      hub += s.hub;
    }
    return { on, hub, total: on + hub };
  };

  const rowSplit = (r: RowKey) => {
    let on = 0;
    let hub = 0;
    for (const g of LEVEL_GROUPS) {
      const s = splitFor(r, g);
      on += s.on;
      hub += s.hub;
    }
    return { on, hub, total: on + hub };
  };

  const disciplineSplit = (disciplineId: string) => {
    let on = 0;
    let hub = 0;
    for (const c of cells) {
      if (c.disciplineId !== disciplineId) continue;
      const s = resolveCellSplit(c);
      on += s.on;
      hub += s.hub;
    }
    return { on, hub, total: on + hub };
  };

  const columnSplit = (group: LevelGroup) => {
    let on = 0;
    let hub = 0;
    for (const c of cells) {
      if (!group.levelIds.includes(c.levelId)) continue;
      const s = resolveCellSplit(c);
      on += s.on;
      hub += s.hub;
    }
    return { on, hub, total: on + hub };
  };

  const grand = cells.reduce(
    (acc, c) => {
      const s = resolveCellSplit(c);
      acc.on += s.on;
      acc.hub += s.hub;
      acc.total += s.total;
      return acc;
    },
    { on: 0, hub: 0, total: 0 },
  );

  const grouped = useMemo(() => {
    const byDisc = new Map<string, RowKey[]>();
    for (const r of rows) {
      const list = byDisc.get(r.disciplineId) || [];
      list.push(r);
      byDisc.set(r.disciplineId, list);
    }
    return Array.from(byDisc.entries());
  }, [rows]);

  const colSpanLevels = LEVEL_GROUPS.length * 2;

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm border-separate border-spacing-0">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0 z-10">
            <tr>
              <th
                rowSpan={2}
                className="text-left align-bottom px-3 py-2 sticky left-0 bg-slate-50 min-w-[180px] border-b border-slate-200"
              >
                Discipline
              </th>
              <th
                rowSpan={2}
                className="text-left align-bottom px-3 py-2 min-w-[140px] border-b border-slate-200"
              >
                Channel
              </th>
              {LEVEL_GROUPS.map((g) => (
                <th
                  key={g.id}
                  colSpan={2}
                  className="text-center px-1.5 pt-2 pb-1 font-semibold text-slate-600 border-b border-slate-100 whitespace-nowrap normal-case tracking-normal"
                >
                  {g.label}
                </th>
              ))}
              <th
                rowSpan={2}
                className="text-right align-bottom px-3 py-2 bg-slate-100/60 border-b border-slate-200"
              >
                On
              </th>
              <th
                rowSpan={2}
                className="text-right align-bottom px-3 py-2 bg-amber-50/60 border-b border-slate-200"
              >
                Hub
              </th>
              <th
                rowSpan={2}
                className="text-right align-bottom px-3 py-2 bg-slate-100/60 border-b border-slate-200"
              >
                Total
              </th>
            </tr>
            <tr className="text-[9px] tracking-wider text-slate-400">
              {LEVEL_GROUPS.map((g) => (
                <Fragmentish key={g.id}>
                  <th className="font-medium text-right pb-2 pl-2 pr-1 border-b border-slate-200 border-l border-slate-100">
                    On
                  </th>
                  <th className="font-medium text-right pb-2 pl-1 pr-2 border-b border-slate-200 bg-amber-50/40 text-amber-700/70">
                    Hub
                  </th>
                </Fragmentish>
              ))}
            </tr>
          </thead>
          <tbody>
            {grouped.map(([disciplineId, discRows], gi) => {
              const ds = disciplineSplit(disciplineId);
              return (
                <Fragmentish key={disciplineId}>
                  <tr className={clsx('bg-slate-50/60', gi > 0 && 'border-t-2 border-slate-200')}>
                    <td className="px-3 py-1.5 text-xs uppercase tracking-wider text-slate-600 font-semibold sticky left-0 bg-slate-50/60">
                      {discLabel(disciplineId)}
                    </td>
                    <td
                      className="px-3 py-1.5 text-xs text-slate-500"
                      colSpan={colSpanLevels + 1}
                    >
                      {discRows.length} row{discRows.length === 1 ? '' : 's'}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-xs font-semibold text-slate-700 bg-slate-100/60">
                      {ds.on.toFixed(1)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-xs font-semibold text-amber-900 bg-amber-50/60">
                      {ds.hub.toFixed(1)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-xs font-semibold text-slate-700 bg-slate-100/60">
                      {ds.total.toFixed(1)}
                    </td>
                  </tr>
                  {discRows.map((r) => {
                    const rs = rowSplit(r);
                    return (
                      <tr
                        key={ROW_KEY(r)}
                        className="border-t border-slate-100 hover:bg-slate-50/40 transition-colors"
                      >
                        <td className="px-3 py-1.5 sticky left-0 bg-white text-slate-700 font-medium text-xs">
                          <span className="pl-2 text-slate-400">↳</span> {discLabel(r.disciplineId)}
                        </td>
                        <td className="px-3 py-1.5 text-slate-500 text-xs">
                          {chanLabel(r.channelId)}
                        </td>
                        {LEVEL_GROUPS.map((g) => {
                          const s = splitFor(r, g);
                          return (
                            <Fragmentish key={g.id}>
                              <td className="px-1.5 py-1.5 text-right tabular-nums text-slate-700 border-l border-slate-100">
                                {fmtCell(s.on)}
                              </td>
                              <td className="px-1.5 py-1.5 text-right tabular-nums text-amber-900 bg-amber-50/30">
                                {fmtCell(s.hub)}
                              </td>
                            </Fragmentish>
                          );
                        })}
                        <td className="px-3 py-1.5 text-right tabular-nums font-medium text-slate-700 bg-slate-50/40">
                          {fmtCell(rs.on)}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums font-medium text-amber-900 bg-amber-50/40">
                          {fmtCell(rs.hub)}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-slate-800 bg-slate-50/40">
                          {fmtCell(rs.total)}
                        </td>
                      </tr>
                    );
                  })}
                </Fragmentish>
              );
            })}
          </tbody>
          <tfoot className="bg-slate-100/60 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <td
                className="px-3 py-2 font-semibold text-slate-700 border-t border-slate-200"
                colSpan={2}
              >
                Column total
              </td>
              {LEVEL_GROUPS.map((g) => {
                const s = columnSplit(g);
                return (
                  <Fragmentish key={g.id}>
                    <td className="px-1.5 py-2 text-right tabular-nums font-semibold text-slate-700 border-l border-slate-100 border-t border-slate-200">
                      {fmtCell(s.on)}
                    </td>
                    <td className="px-1.5 py-2 text-right tabular-nums font-semibold text-amber-900 bg-amber-50/40 border-t border-slate-200">
                      {fmtCell(s.hub)}
                    </td>
                  </Fragmentish>
                );
              })}
              <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700 border-t border-slate-200">
                {grand.on.toFixed(1)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold text-amber-900 bg-amber-50/60 border-t border-slate-200">
                {grand.hub.toFixed(1)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-bold text-brand-purple-700 border-t border-slate-200">
                {grand.total.toFixed(1)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="flex items-center gap-4 text-[10px] uppercase tracking-wider text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm border border-slate-200 bg-white" />
          On — onshore FTE
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm border border-amber-200 bg-amber-50/60" />
          Hub — offshore + nearshore FTE
        </span>
        <span className="text-slate-400 normal-case tracking-normal">
          Empty cells show ·
        </span>
      </div>
    </div>
  );
}

function Fragmentish({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
